import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createGroupAsync, clearError, fetchGroupsAsync } from '../store/slices/groupsSlice';
import { useLevels } from '../hooks/useLevels';
import { bulkCreateStudentsAsync, clearError as clearStudentsError } from '../store/slices/studentsSlice';
import LoadingSpinner from './LoadingSpinner';
import './GroupForm.css';

interface Schedule {
  dayOfWeek: string;
  time: string;
}

interface Student {
  firstName: string;
  lastName: string;
  surname: string;
  contacts: string;
  startDate: string;
}

const CreateGroup: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, groups: allGroups } = useAppSelector((state) => state.groups);
  const { loading: studentsLoading, error: studentsError } = useAppSelector((state) => state.students);

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    startDate: '',
    endDate: '',
    offerNumber: '',
    parentGroupId: '',
  });

  const [schedules, setSchedules] = useState<Schedule[]>([
    { dayOfWeek: '', time: '' }
  ]);

  const [students, setStudents] = useState<Student[]>([
    { firstName: '', lastName: '', surname: '', contacts: '', startDate: '' }
  ]);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load all groups for Parent Group dropdown
  useEffect(() => {
    dispatch(fetchGroupsAsync({ page: 1, limit: 1000 }));
  }, [dispatch]);

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const levels = useLevels();

  // Format date as yyyy-mm-dd (no brackets), handling ISO strings with/without time
  const toYMD = (dateInput?: string | Date | null) => {
    if (!dateInput) return '';
    if (typeof dateInput === 'string') {
      if (dateInput.includes('T')) return dateInput.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) return dateInput;
    }
    const d = new Date(dateInput as Date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear form error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const formatTimeInput = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as HH:MM
    if (digits.length >= 3) {
      return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    } else if (digits.length >= 1) {
      return digits;
    }
    return '';
  };



  const handleScheduleChange = (index: number, field: 'dayOfWeek' | 'time', value: string) => {
    const newSchedules = [...schedules];
    
    if (field === 'time') {
      // Format the time input
      const formattedTime = formatTimeInput(value);
      newSchedules[index] = {
        ...newSchedules[index],
        [field]: formattedTime
      };
    } else {
      newSchedules[index] = {
        ...newSchedules[index],
        [field]: value
      };
    }
    
    setSchedules(newSchedules);
  };

  const addSchedule = () => {
    setSchedules([...schedules, { dayOfWeek: '', time: '' }]);
  };

  const removeSchedule = (index: number) => {
    if (schedules.length > 1) {
      setSchedules(schedules.filter((_, i) => i !== index));
    }
  };

  const handleStudentChange = (index: number, field: keyof Student, value: string) => {
    const newStudents = [...students];
    newStudents[index] = {
      ...newStudents[index],
      [field]: value
    };
    setStudents(newStudents);
  };

  const addStudent = () => {
    setStudents([...students, { firstName: '', lastName: '', surname: '', contacts: '', startDate: '' }]);
  };

  const removeStudent = (index: number) => {
    if (students.length > 1) {
      setStudents(students.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.level) {
      errors.level = 'Level is required';
    }

    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) <= new Date(formData.startDate)) {
        errors.endDate = 'End date must be after start date';
      }
    }

    // Validate schedules
    const validSchedules = schedules.filter(schedule => 
      schedule.dayOfWeek && schedule.time
    );

    if (validSchedules.length === 0) {
      errors.schedules = 'At least one complete schedule is required';
    }

    // Check for duplicate day/time combinations
    const scheduleKeys = validSchedules.map(s => `${s.dayOfWeek}-${s.time}`);
    const uniqueKeys = new Set(scheduleKeys);
    if (scheduleKeys.length !== uniqueKeys.size) {
      errors.schedules = 'Duplicate schedule entries are not allowed';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    if (error) dispatch(clearError());
    if (studentsError) dispatch(clearStudentsError());
    
    if (!validateForm()) {
      return;
    }

    const validSchedules = schedules.filter(schedule => 
      schedule.dayOfWeek && schedule.time
    );

    const groupData = {
      name: formData.name.trim() || undefined,
      level: formData.level,
      startDate: formData.startDate,
      ...(formData.endDate ? { endDate: formData.endDate } : {}),
      schedules: validSchedules,
      ...(formData.offerNumber ? { offerNumber: parseInt(formData.offerNumber, 10) } : {}),
      ...(formData.parentGroupId ? { parentGroupId: parseInt(formData.parentGroupId, 10) } : {}),
    };

    try {
      // Get the full response to access the message and attachedStudentsCount
      const response = await dispatch(createGroupAsync(groupData)).unwrap() as {
        group: { id: number };
        attachedStudentsCount?: number;
        message?: string;
      };
      const createdGroup = response.group;
      const attachedCount = response.attachedStudentsCount || 0;

      // Show success message if students were auto-attached from parent group
      if (attachedCount > 0) {
        console.log(`✅ ${response.message || `${attachedCount} students attached from parent group`}`);
      }

      // Create students if any are provided
      const validStudents = students.filter(student => 
        student.firstName.trim() && student.lastName.trim() && student.startDate
      );

      if (validStudents.length > 0) {
        try {
          await dispatch(bulkCreateStudentsAsync({
            students: validStudents.map(student => ({
              firstName: student.firstName.trim(),
              lastName: student.lastName.trim(),
              contacts: student.contacts.trim() || undefined,
              startDate: student.startDate,
              groupId: createdGroup.id
            })),
            groupId: createdGroup.id
          })).unwrap();
        } catch (studentError) {
          // Student creation failed, but group was created successfully
          // Show error message but don't redirect yet
          console.error('Failed to create students:', studentError);
          return; // Don't navigate, let user see the error and try again
        }
      }

      // Navigate to the edit page of the newly created group
      navigate(`/groups/edit/${createdGroup.id}`);
    } catch (error) {
      // Group creation failed - error is handled by the slice
    }
  };

  const handleCancel = () => {
    navigate('/groups');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Create New Group</h1>
          <p>Add a new student group with schedule information</p>
        </div>
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {studentsError && (
        <div className="error-message">
          Students Error: {studentsError}
          <button onClick={() => dispatch(clearStudentsError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-group">
            <label htmlFor="name">Group Name (Optional)</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter group name (optional)"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="offerNumber">Offer Number</label>
            <input
              type="number"
              id="offerNumber"
              name="offerNumber"
              value={formData.offerNumber}
              onChange={handleInputChange}
              placeholder="Auto-assigned (next in series) if left empty"
              className="form-input"
              min={1}
            />
          </div>

          <div className="form-group">
            <label htmlFor="parentGroupId">Parent Group (Optional)</label>
            <select
              id="parentGroupId"
              name="parentGroupId"
              value={formData.parentGroupId}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">None</option>
              {allGroups.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name ? `${g.name} (${g.id})` : `Group #${g.id}`} — {g.level.toUpperCase()} — {toYMD(g.startDate)} - {g.endDate ? toYMD(g.endDate) : '-'}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="level">Level *</label>
            <select
              id="level"
              name="level"
              value={formData.level}
              onChange={handleInputChange}
              className={`form-select ${formErrors.level ? 'error' : ''}`}
              required
            >
              <option value="">Select level</option>
              {levels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
            {formErrors.level && <span className="error-text">{formErrors.level}</span>}
          </div>

          <div className="form-row-compact">
            <div className="form-group">
              <label htmlFor="startDate">Start Date *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`form-input ${formErrors.startDate ? 'error' : ''}`}
                required
              />
              {formErrors.startDate && <span className="error-text">{formErrors.startDate}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="endDate">End Date (Optional)</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`form-input ${formErrors.endDate ? 'error' : ''}`}
              />
              {formErrors.endDate && <span className="error-text">{formErrors.endDate}</span>}
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Schedules</h2>
            <button type="button" onClick={addSchedule} className="btn-add-schedule">
              + Add Schedule
            </button>
          </div>
          {formErrors.schedules && (
            <div className="error-text schedule-error">{formErrors.schedules}</div>
          )}
          <table className="schedule-table">
            <thead>
              <tr>
                <th>Day of Week</th>
                <th>Time</th>
                <th style={{ width: '50px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={schedule.dayOfWeek}
                      onChange={(e) => handleScheduleChange(index, 'dayOfWeek', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select day</option>
                      {daysOfWeek.map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={schedule.time}
                      onChange={(e) => handleScheduleChange(index, 'time', e.target.value)}
                      className="form-input"
                      pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                      placeholder="HH:MM (24-hour format)"
                      title="Please enter time in 24-hour format (HH:MM)"
                      maxLength={5}
                    />
                  </td>
                  <td>
                    {schedules.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSchedule(index)}
                        className="btn-remove-schedule"
                        title="Remove this schedule"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Students (Optional)</h2>
            <button type="button" onClick={addStudent} className="btn-add-schedule">
              + Add Student
            </button>
          </div>
          <div className="students-list">
            {students.map((student, index) => (
              <div key={index} className="student-item">
                <div className="student-form">
                  <div className="form-row-compact three-columns">
                    <div className="form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        value={student.firstName}
                        onChange={(e) => handleStudentChange(index, 'firstName', e.target.value)}
                        className="form-input"
                        placeholder="Enter first name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        value={student.lastName}
                        onChange={(e) => handleStudentChange(index, 'lastName', e.target.value)}
                        className="form-input"
                        placeholder="Enter last name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Surname</label>
                      <input
                        type="text"
                        value={student.surname}
                        onChange={(e) => handleStudentChange(index, 'surname', e.target.value)}
                        className="form-input"
                        placeholder="Enter surname (optional)"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Contacts</label>
                    <textarea
                      value={student.contacts}
                      onChange={(e) => handleStudentChange(index, 'contacts', e.target.value)}
                      className="form-input"
                      placeholder="Enter contact information (phone, email, etc.)"
                      rows={2}
                    />
                  </div>

                  <div className="form-group">
                    <label>First Payment Date</label>
                    <input
                      type="date"
                      value={student.startDate}
                      onChange={(e) => handleStudentChange(index, 'startDate', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  {students.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStudent(index)}
                      className="btn-remove-schedule"
                      title="Remove this student"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-success"
            disabled={loading || studentsLoading}
          >
            {loading || studentsLoading ? 'Creating...' : 'Create Group'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateGroup; 